import {
  createBrowserRouter,
  createMemoryRouter,
  Navigate,
  type RouteObject,
  useLocation,
} from 'react-router-dom';
import { NAV_PAGES } from './navigation';
import { useConsoleApi } from './ConsoleApiContext';
import { useSessionSnapshot } from '../lib/capability';
import { AppShell } from '../components/AppShell';
import { lazyPage } from './lazyPage';
import { LoginPage } from '../pages/LoginPage';
import { PendingPage } from '../pages/PendingPage';

/**
 * The fourteen live pages, each in its own chunk (the plan's ruling E).
 *
 * The mapper (`.then((m) => ({ default: m.X }))`) is what lets the pages keep their **named**
 * exports — their own tests import them by name, and fourteen `export default` lines would be
 * fourteen files touched to satisfy one lazy call. One file changes instead of fourteen.
 *
 * `LoginPage` and `PendingPage` stay eager on purpose: the session gate must not wait behind a
 * chunk fetch, and the `*` fallback of a route that is itself a fallback must never be the thing
 * that fails to load.
 */
const DashboardPage = lazyPage('dashboard', () =>
  import('../pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const DevicesPage = lazyPage('devices', () =>
  import('../pages/DevicesPage').then((m) => ({ default: m.DevicesPage })),
);
const DeviceDetailPage = lazyPage('device-detail', () =>
  import('../pages/DeviceDetailPage').then((m) => ({ default: m.DeviceDetailPage })),
);
const ActivationsPage = lazyPage('activations', () =>
  import('../pages/ActivationsPage').then((m) => ({ default: m.ActivationsPage })),
);
const DiagnosticsPage = lazyPage('diagnostics', () =>
  import('../pages/DiagnosticsPage').then((m) => ({ default: m.DiagnosticsPage })),
);
const AlarmsPage = lazyPage('alarms', () =>
  import('../pages/AlarmsPage').then((m) => ({ default: m.AlarmsPage })),
);
const OltsPage = lazyPage('olts', () =>
  import('../pages/OltsPage').then((m) => ({ default: m.OltsPage })),
);
const FirmwarePage = lazyPage('firmware', () =>
  import('../pages/FirmwarePage').then((m) => ({ default: m.FirmwarePage })),
);
const AuditPage = lazyPage('audit', () =>
  import('../pages/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const KeysPage = lazyPage('keys', () =>
  import('../pages/KeysPage').then((m) => ({ default: m.KeysPage })),
);
const JobsPage = lazyPage('jobs', () =>
  import('../pages/JobsPage').then((m) => ({ default: m.JobsPage })),
);
const ProfilePage = lazyPage('profile', () =>
  import('../pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const SettingsPage = lazyPage('settings', () =>
  import('../pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const SystemPage = lazyPage('system', () =>
  import('../pages/SystemPage').then((m) => ({ default: m.SystemPage })),
);
const TemplatesPage = lazyPage('templates', () =>
  import('../pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
const ReconciliationPage = lazyPage('reconciliation', () =>
  import('../pages/ReconciliationPage').then((m) => ({ default: m.ReconciliationPage })),
);

/**
 * Router: `/` + every plan page `navigation.ts` registers + the login screen.
 *
 * Live pages get their own component; every other plan page gets the same
 * `PendingPage` shell keyed by its path, so the nav can never link to a blank
 * route and a newly-built page only needs one line changed here. The tree is
 * exported so tests mount the *real* routes (shell, breadcrumb and all) through a
 * memory router instead of a parallel copy.
 *
 * The live pages are **lazy**: each one is fetched as its own chunk when the
 * operator first walks into it, behind the single `Suspense` in the shell.
 */
export const consoleRoutes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: <RequireSession />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'devices', element: <DevicesPage /> },
      { path: 'devices/:deviceId', element: <DeviceDetailPage /> },
      { path: 'activations', element: <ActivationsPage /> },
      { path: 'diagnostics', element: <DiagnosticsPage /> },
      { path: 'alarms', element: <AlarmsPage /> },
      { path: 'olts', element: <OltsPage /> },
      { path: 'firmware-campaigns', element: <FirmwarePage /> },
      { path: 'templates', element: <TemplatesPage /> },
      { path: 'reconciliation', element: <ReconciliationPage /> },
      { path: 'audit', element: <AuditPage /> },
      { path: 'jobs', element: <JobsPage /> },
      { path: 'keys', element: <KeysPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'system', element: <SystemPage /> },
      ...NAV_PAGES.filter((page) => page.status === 'pending').map((page) => ({
        path: page.path,
        element: <PendingPage />,
      })),
      { path: '*', element: <PendingPage /> },
    ],
  },
];

/**
 * The gate `VITE_DATA_SOURCE=http` was waiting for (U-08-f).
 *
 * Live mode with no session renders the login screen instead of a page full of 401s — the
 * provider used to `throw` here, which is how the console ended up never having been connected
 * to a real ACS (`docs/DEVELOPMENT_PLAN_FA.md:366`). Fixture mode is deliberately NOT gated:
 * the demo path and the existing page tests render sample data with nobody signed in, and a
 * login there is something an operator can do, not something the console demands.
 *
 * A 401 from any door clears the session in `api/session.ts`, so a revocation lands the
 * operator back here rather than on a page that still looks healthy.
 */
function RequireSession() {
  const api = useConsoleApi();
  const snapshot = useSessionSnapshot();
  const location = useLocation();
  if (api.dataSource === 'http' && snapshot.status !== 'signed-in') {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }
  return <AppShell />;
}

export function createConsoleRouter(base: string): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(consoleRoutes, { basename: base });
}

/** Used by tests (and any embedding host) to mount a specific route in memory. */
export function createTestRouter(initialPath: string) {
  return createMemoryRouter(consoleRoutes, { initialEntries: [initialPath] });
}
